import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  EventEmitter,
  Output,
  Input,
  forwardRef,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  HostListener
} from '@angular/core';
import { DropDownSelectorItem } from '../../models/dropDownSelectorItem';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  DxDataGridComponent,
  DxDropDownBoxComponent
} from 'devextreme-angular';
import { fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { GuidService } from '../../services/guid.service';
import { DropDownPosition } from '../../models/dropdownPosition';

@Component({
    selector: 'app-drop-down-box',
    templateUrl: './drop-down-box.component.html',
    styleUrls: ['./drop-down-box.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DropdownBoxComponent),
            multi: true
        }
    ],
    standalone: false
})
export class DropdownBoxComponent implements OnInit, ControlValueAccessor, OnChanges {
  isDropDownOpen: boolean;
  @HostListener('document:wheel', ['$event']) onMousewheel(event) {
    if (this.isDropDownOpen) {
      let isScrollBarVisible = false;
      let isInside = false;
      let target = event.target;
      while(target && !isInside) {
        isScrollBarVisible = document.body.clientHeight <= target.clientHeight || isScrollBarVisible;
        isInside = target.classList.contains('dropDownGrid');
        target = target.parentElement;
      }
      if (!isInside && isScrollBarVisible) {
        this.dropDownBox.instance.close();
      }
    }
  }

  @ViewChild('searchBox') searchBox: ElementRef;
  @ViewChild('dropDownGrid') dataGrid: DxDataGridComponent;
  @ViewChild('dropDownBox') dropDownBox: DxDropDownBoxComponent;

  @Output() selectionChanged = new EventEmitter();
  @Output() selectedItemIdChange = new EventEmitter<number>();
  @Output() selectedItemsIdsChange = new EventEmitter<number[]>();

  @Input() isMultiselect: boolean;
  @Input() validationRules: [];
  @Input() isDisabled: boolean;
  @Input() popupId: boolean;
  @Input() dataSource: DropDownSelectorItem[];
  @Input() dropDownColumns: string[];
  @Input() placeholder: string;
  @Input() showButtons = false;
  @Input() isDataSourceLoading = false;
  @Input() position: DropDownPosition = {my: 'right top', at: 'right top', offset: '0 45'};

  readonly maxDropdownDataGridHeight : number = 18;
  readonly dropdownDataGridElementHeight : number = 3;

  selectedIds: number[] = [];
  previouslySelectedItemIds: number[] = [];
  searchInitialized: boolean;
  dropDownInitialized: boolean;
  selectionMode: object;
  dropDownBoxId: string;
  columns: string[];
  canSelectAll: boolean = true;
  justSomeValuesSelected: boolean = false;
  filtered: boolean = false;
  undefinedBooleanValue: boolean;
  get undefinedBoolean() { // this is used to diplays the correct style always for the second devextreme checkbox. it should always remain undefined
    return this.undefinedBooleanValue;
  }
  set undefinedBoolean(val) {
    this.selectAllChecked = true;
  }

  selectedItemIdValue: number;
  @Input() get selectedItemId() {
    return this.selectedItemIdValue;
  }
  set selectedItemId(val) {
    if (val !== undefined && val !== this.selectedItemId) {
      this.selectedItemIdValue = val;
      this.selectedItemIdChange.emit(this.selectedItemIdValue);
      this.selectedIds = [];
      this.selectedIds.push(val);
    }
  }

  isIncludedInTitleValue: boolean;
  @Input() get isIncludedInTitle() {
    return this.isIncludedInTitleValue;
  }
  set isIncludedInTitle(val) {
    if (val === undefined) {
      this.isIncludedInTitleValue = false;
    }
    if (val !== undefined) {
      this.isIncludedInTitleValue = val;
    }
  }

  updatingAllChecked: boolean;
  selectAllCheckedValue: boolean = false;
  selectedItemsIdsValue: number[];
  @Input() get selectedItemsIds() {
    return this.selectedItemsIdsValue;
  }
  set selectedItemsIds(val) {
    if (this.selectedItemsIdsValue !== val) {
      this.selectedIds = val;
      this.selectedItemsIdsValue = val;
      this.selectedItemsIdsChange.emit(this.selectedItemsIdsValue);
      this.updateSelectAllChecked();
      this.justSomeValuesSelected = !this.selectAllChecked && this.selectedItemsIdsValue.length > 0;
    }
  }

  updateSelectAllChecked() {
    if (this.isMultiselect && this.dataGrid) {
      this.updatingAllChecked = true;
      this.loadFilteredData((result) => {
        let selectAllChecked = result !== undefined && result.length > 0;
        if (this.selectedItemsIds === undefined) {
          this.selectedItemsIds = [];
        }
        if (result) {
          result.forEach(i => {
            if (this.selectedItemsIds.find(s => s === i.id) === undefined) {
              selectAllChecked = false;
            }
          });
        }
        this.selectAllChecked = selectAllChecked;
        this.justSomeValuesSelected = !this.selectAllChecked && this.selectedItemsIdsValue.length > 0;
        this.updatingAllChecked = false;
      });
    }
  }

  get selectAllChecked() {
    return this.selectAllCheckedValue;
  }

  set selectAllChecked(val) {
    if (this.selectAllChecked !== val) {
      this.selectAllCheckedValue = val;

      if (!this.updatingAllChecked) {
        this.loadFilteredData((result) => {
          let newSelectedItemsIds = [...this.selectedItemsIds];
          result.forEach(element => {
            if (this.selectAllCheckedValue === true && this.selectedItemsIds.find(i => i == element.id) === undefined) {
              newSelectedItemsIds.push(element.id);
            } else if (this.selectAllCheckedValue === false && this.selectedItemsIds.find(i => i == element.id) !== undefined) {
              newSelectedItemsIds = newSelectedItemsIds.filter(i => i !== element.id);
            }
          });
          this.selectedItemsIds = newSelectedItemsIds;
        });
      }
    }
  }

  constructor(private readonly cd: ChangeDetectorRef) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.dataSource) {
      this.dataSource = changes.dataSource.currentValue;
      this.updateSelectAllChecked();
    }
  }

  ngOnInit() {
    if (this.placeholder === undefined) {
      this.placeholder = '';
    }
    this.dropDownBoxId = GuidService.newGuid();
    this.selectionMode = this.isMultiselect
      ? { mode: 'multiple', showCheckBoxesMode: 'always' }
      : { mode: 'single' };
    if (this.dropDownColumns) {
      this.columns = this.dropDownColumns;
    } else {
      this.columns = ['name'];
    }
  }

  onSelectionChanged() {
    if (!this.showButtons) {
      if (this.isMultiselect) {
        this.selectedItemsIds = this.selectedIds;
        this.onChange(this.selectedItemsIds);
      }
      else {
        this.selectedItemId = this.selectedIds[0];
        this.dropDownBox.instance.close();
        this.onTouch();
        this.onChange(this.selectedItemId);
      }
      this.selectionChanged.emit();
    } else {
      this.selectedItemsIds = this.selectedIds;
      this.onChange(this.selectedItemsIds);
    }
  }

  onDropdownOpened() {
    this.isDropDownOpen = true;
    this.dropDownInitialized = true;
    this.initializeSearch();
    this.previouslySelectedItemIds = this.selectedItemsIds ? [...this.selectedItemsIds] : [];
    this.updateSelectAllChecked();
  }

  onCancel() {
    this.cancelTheSelection();
    this.dropDownBox.instance.close();
    this.onTouch();
  }

  onOK() {
    this.dropDownBox.instance.close();
    this.onTouch();
  }

  cancelTheSelection() {
    if (!this.previouslySelectedItemIds.length) {
      this.selectedItemsIds = [];
    }

    if (this.previouslySelectedItemIds.length !== this.selectedItemsIds.length ||
      ((this.previouslySelectedItemIds.length === this.selectedItemsIds.length) &&
        JSON.stringify(this.getSortedArray(this.previouslySelectedItemIds)) !==
        JSON.stringify(this.getSortedArray(this.selectedItemsIds)))) {
      this.selectedItemsIds = [...this.previouslySelectedItemIds];
      this.cd.detectChanges();
    }
  }

  saveTheSelection() {
    if (this.previouslySelectedItemIds.length !== this.selectedItemsIds.length ||
      ((this.previouslySelectedItemIds.length === this.selectedItemsIds.length) &&
        JSON.stringify(this.getSortedArray(this.previouslySelectedItemIds)) !==
        JSON.stringify(this.getSortedArray(this.selectedItemsIds)))) {
      this.onChange(this.selectedItemsIds);
      this.selectionChanged.emit();
    }
  }

  onDropdownClosed() {
    this.isDropDownOpen = false;
    if (this.isMultiselect && this.showButtons) {
        this.saveTheSelection();
    }
    this.dropDownBox.instance.close();
    this.onTouch();
  }

  getSortedArray(array: number[]) {
    return array.sort((a, b) => a - b);
  }

  initializeSearch() {
    if (!this.searchInitialized) {
      this.searchInitialized = true;
      fromEvent(this.searchBox.nativeElement, 'keyup')
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          tap(() => {
              this.dataGrid.instance.filter(this.getFilterExpression());
              this.loadFilteredData((result) => {
                this.canSelectAll = result && result.length > 0;
              });
              this.updateSelectAllChecked();
              this.cd.detectChanges();
          })
        ).subscribe();
    }
  }

  calculateDropDownDataGridHeight() {
    let height = 0;
    if (this.dataSource?.length) {
      height = this.dataSource.length * this.dropdownDataGridElementHeight;
      if (height > this.maxDropdownDataGridHeight) {
        height = this.maxDropdownDataGridHeight;
      }
    }

    return `${height}rem`;
  }

  getFilterExpression() {
    this.filtered = this.searchBox.nativeElement.value !== '';
    return [ "name", "contains" ,this.searchBox.nativeElement.value];
  }

  loadFilteredData(callback) {
    this.dataGrid.instance
          .getDataSource()
          .store()
          .load({ filter: this.getFilterExpression() })
          .then((result) => {
            callback(result);
          });
  }

  getPlaceHolder() {
    return this.placeholder.toUpperCase();
  }

  onChange: any = () => { }

  onTouch: any = () => { }

  writeValue(value: any) {
    if (value !== null) {
      this.selectedItemId = value;
    }
    else {
      this.selectedItemId = undefined;
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouch = fn;
  }
}
